import type { Translate } from "@similarweb/i18n-provider";
import {
    formatDate,
    shortMonthDayAndYear,
    TextCell,
    TruncatedTextCell,
    type ColumnsType,
} from "@similarweb/pro-logic-components";

import { VisibilityBadge } from "app/pages/sales-intelligence/common-components/VisibilityBadge/VisibilityBadge";
import { NO_DATA_HYPHEN } from "app/pages/sales-intelligence/constants/common";
import { isSharedEmployeeProfile } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/helpers/accessLevelFilters";
import type { ProfileDto } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/types/dto";

import { getEmployeeProfileUpdatedDate } from "../../utils/splitEmployeeProfiles";

import { EmployeeProfilesTableActionsCell } from "./EmployeeProfilesTableActionsCell";

export type EmployeeProfilesColumnHandlers = {
    onEdit: (profile: ProfileDto) => void;
    onDelete: (profile: ProfileDto) => void;
};

type GetEmployeeProfilesColumnsOptions = {
    handlers: EmployeeProfilesColumnHandlers;
    showCreatorName: boolean;
    showRowActions: boolean;
};

/** Values keep the order the user picked them in, and collapse to an em dash when nothing is set. */
const joinCriteriaValues = (values: readonly string[]): string => (values.length ? values.join(", ") : NO_DATA_HYPHEN);

export const getEmployeeProfilesColumns = (
    translate: Translate,
    { handlers, showCreatorName, showRowActions }: GetEmployeeProfilesColumnsOptions,
): ColumnsType<ProfileDto> => [
    {
        dataIndex: "name",
        title: translate("si.employee-profiles.column.name"),
        width: 300,
        render: (_value, record) => <TruncatedTextCell>{record.name ?? ""}</TruncatedTextCell>,
    },
    ...(showCreatorName
        ? ([
              {
                  dataIndex: "creatorName",
                  title: translate("si.employee-profiles.column.created_by"),
                  width: 160,
                  render: (_value, record) => (
                      <TruncatedTextCell>{record.creatorName || NO_DATA_HYPHEN}</TruncatedTextCell>
                  ),
              },
          ] satisfies ColumnsType<ProfileDto>)
        : []),
    {
        dataIndex: "departments",
        title: translate("si.employee-profiles.column.department"),
        render: (_value, record) => <TruncatedTextCell>{joinCriteriaValues(record.departments)}</TruncatedTextCell>,
    },
    {
        dataIndex: "seniority",
        title: translate("si.employee-profiles.column.seniority"),
        render: (_value, record) => <TruncatedTextCell>{joinCriteriaValues(record.seniority)}</TruncatedTextCell>,
    },
    {
        dataIndex: "accessLevel",
        title: translate("si.employee-profiles.column.type"),
        width: 110,
        render: (_value, record) => {
            const isShared = isSharedEmployeeProfile(record);

            return (
                <VisibilityBadge
                    isShared={isShared}
                    dataAutomationSuffix={record.id}
                />
            );
        },
    },
    {
        dataIndex: "updatedTime",
        title: translate("si.employee-profiles.column.updated"),
        width: 140,
        render: (_value, record) => {
            const updatedDate = getEmployeeProfileUpdatedDate(record);

            return (
                <TextCell>
                    {updatedDate ? formatDate(updatedDate, undefined, shortMonthDayAndYear) : NO_DATA_HYPHEN}
                </TextCell>
            );
        },
    },
    ...(showRowActions
        ? ([
              {
                  key: "actions",
                  title: "",
                  width: 108,
                  render: (_value, record) => (
                      <EmployeeProfilesTableActionsCell
                          profile={record}
                          onEdit={handlers.onEdit}
                          onDelete={handlers.onDelete}
                      />
                  ),
              },
          ] satisfies ColumnsType<ProfileDto>)
        : []),
];
